import axiosInstance from '@/shared/services/api/axios.api';

interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropOptions {
  quality?: number;
  format?: 'image/jpeg' | 'image/png' | 'image/webp';
}

type ImageSource = File | string | Blob;

const loadImage = async (id: string): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(`medias/${id}/proxy`, {
      responseType: 'blob',
    });

    if (!(response.data instanceof Blob)) {
      throw new Error('Response is not a blob');
    }

    return response.data;
  } catch (error) {
    console.error('Failed to load image:', error);
    throw error;
  }
};

export const loadAndCrop = async (selectedFileId: string, cropData: CropData, options: CropOptions = {}) => {
  const blob = await loadImage(selectedFileId as string);

  return cropImageProgrammatically(blob, cropData, options);
};

export const cropImageProgrammatically = (
  imageSource: ImageSource,
  cropData: CropData,
  options: CropOptions = {}
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = (): void => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      canvas.width = cropData.width;
      canvas.height = cropData.height;

      ctx.drawImage(
        img,
        cropData.x,
        cropData.y,
        cropData.width,
        cropData.height,
        0,
        0,
        cropData.width,
        cropData.height
      );

      canvas.toBlob(
        (blob: Blob | null) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        options.format || 'image/jpeg',
        options.quality || 0.9
      );
    };

    img.onerror = (error: string | Event): void => {
      reject(new Error(`Failed to load image: ${error}`));
    };

    if (imageSource instanceof File) {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>): void => {
        if (e.target?.result && typeof e.target.result === 'string') {
          img.src = e.target.result;
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = (): void => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(imageSource);
    } else if (imageSource instanceof Blob) {
      const url = URL.createObjectURL(imageSource);
      img.src = url;

      const cleanup = () => URL.revokeObjectURL(url);
      img.addEventListener('load', cleanup, { once: true });
      img.addEventListener('error', cleanup, { once: true });
    } else {
      img.src = imageSource;
    }
  });
};

export const cropVertical = async (selectedFileId: string, width: number, height: number): Promise<Blob | null> => {
  const blob = await loadImage(selectedFileId as string);

  try {
    const aspectRatio = 9 / 16;

    let cropWidth: number;
    let cropHeight: number;

    const maxWidthForHeight = height * aspectRatio;
    const maxHeightForWidth = width / aspectRatio;

    if (maxWidthForHeight <= width) {
      cropHeight = height;
      cropWidth = Math.round(height * aspectRatio);
    } else {
      cropWidth = width;
      cropHeight = Math.round(width / aspectRatio);
    }

    const cropData: CropData = {
      x: Math.round((width - cropWidth) / 2),
      y: Math.round((height - cropHeight) / 2),
      width: cropWidth,
      height: cropHeight,
    };

    const options: CropOptions = {
      quality: 0.9,
      format: 'image/jpeg',
    };

    return await cropImageProgrammatically(blob, cropData, options);
  } catch (error) {
    console.error('Cropping failed:', error);
    return null;
  }
};
